// ═══════════════════════════════════════════════════════════════════════════
//  THE ATTENDANCE REGISTER — the Administrator's screen, and the only screen in
//  CCA-decides that WRITES.
//
//  Registrar ruling, 2026-08-15, verbatim: "The Admin role is to confirm the
//  attendees in the list of eligible voters - this should be updatable as the
//  time progresses. Therefore you will know the correct voting number at every
//  stage."
//
//  ADMIN ONLY, AND THE SERVER DECIDES THAT. `GET|POST /api/register/:electionId`
//  resolves a session, a `Contacts.id` and a `role_grant` row and then asks a
//  fifth question the results route does not ask — is this person's strongest
//  role `admin`? A scrutineer WATCHES a sitting and does not RUN it, and is
//  refused `not_administrator` here while still being served a counted results
//  sheet. This component enforces none of that and cannot: it is a renderer, and
//  a renderer draws whatever it is handed.
// ═══════════════════════════════════════════════════════════════════════════
//
// ── A CORRECTION IS A FURTHER EVENT, NEVER AN EDIT ─────────────────────────
// `attendance_event` is append-only and hash-chained from the frozen roll's
// published digest, so ticking a member present and then correcting it to absent
// writes TWO rows and both stay. **This screen therefore has no checkbox**, and
// that absence is the design rather than an omission: a checkbox is a control
// over a value, and there is no value here to be over — there is a chain, and a
// projection of it. Each row carries two buttons that APPEND, the current
// projection is shown as a state and not as a form field, and the chain position
// is on the page so a reader can see it move.
//
// ── THE NUMBERS ARE PER HOUSE, AND THERE IS NO TOTAL ───────────────────────
// SO 12.1 is a per-House test. `0004_attendance.sql` invariant 5: "a sum is
// satisfied by one House carrying the whole roll", which is the exact shape a
// partial read produces. So the table below has no Total column and no aggregate
// row, and if you are here to add one: the number you would put in it cannot be
// read against the rule by anybody.
//
// THE BISHOP IS A COLUMN HERE AND IS NOT ONE ON THE RESULTS SHEET, and the
// asymmetry is deliberate. There, a one-person tally is an ATTRIBUTION of a
// secret ballot (SO 15.9) and engine version 2 dissolved it. Here the fact is
// whether a man is in the room, which is not a secret and which
// `roll_snapshot.summoned_bishop` and `ballot.quorum_claimed_bishop` both already
// record as their own number. He is counted on his own — never folded into a
// House (registrar ruling 2026-08-14).
//
// ── AND IT DECLARES NOTHING ────────────────────────────────────────────────
// **There is no verdict on this page and no word "quorate" anywhere in this
// file.** Quorum is a DECLARED act of the Administrator (registrar ruling
// 2026-08-16), recorded in the six `quorum_*` columns on `ballot`, and NOTHING in
// the schema verifies those numbers — deliberately. This screen is how the Admin
// KNOWS; the declaration is a separate act, made on a ballot, at the moment a
// vote is enlivened. A page that computed the verdict would be reinstating
// exactly the derivation the ruling struck.
//
// It shows NO VOTE FIGURE either. Attendance and results are two books, and
// nothing on this screen comes from `participation`, `mark`, `paper` or
// `count_run`.
//
// ── THE ONE THING THAT IS MISSING, SAID PLAINLY ────────────────────────────
// **THERE ARE NO NAMES, BECAUSE THIS DATABASE HOLDS NONE.** `elector` has no
// name column and no email column, by design: a display name is tier-3 data that
// lives in Zoho, and keeping it out of the roll is what stops a name correction
// moving the published roll digest (`@cca/decides-roll`'s `ElectorReviewDetail`
// says so in terms). What this register can honestly show today is the Zoho
// `Contacts.id` — an identifier, tier 2 — and the elector id. That is enough to
// see the shape of the screen and it is NOT enough to run a door with. The join
// that fixes it is recorded in the README under "Owed".

import { useCallback, useEffect, useState, type JSX, type ReactNode } from 'react';
import { PortalHeader } from '@cca/ui';
import { ModeBand } from '../results/ModeBand';
import { portalNameFor } from '../portal-name';
import {
  api,
  DecidesApiError,
  type Identity,
  type Presence,
  type RegisterElector,
  type RegisterHouse,
  type RegisterHouseCount,
  type RegisterView,
} from '../api/client';

/** The Houses in the order every surface on this platform states them. */
const HOUSE_LABEL: Record<RegisterHouse, string> = {
  clergy: 'Clergy',
  laity: 'Laity',
  bishop: 'Bishop',
};

/** The three states, as words. **`null` is its own sentence and never "no".**
 *  Unrecorded is what separates "the room is thin" from "the register was never
 *  finished" — both look the same from a count, and only one of them is a fact
 *  about Synod. */
const PRESENCE_WORD: Record<'present' | 'absent' | 'unrecorded', string> = {
  present: 'Present',
  absent: 'Absent',
  unrecorded: 'Not yet recorded',
};

/**
 * A refusal turned into a sentence a person can act on. The wire codes are
 * machine-facing and deliberately distinct so an operator can tell different
 * problems apart; not one of them is a sentence to show anybody.
 *
 * `stood_down` and `officer` are the two that matter most, and each ends with
 * what to do instead — a screen that can only say no teaches an operator to work
 * around the fence.
 */
const REFUSAL_COPY: Record<string, string> = {
  unauthorized: 'Your session has ended. Sign in again.',
  not_found: 'There is no sitting with that identifier.',
  no_grant: 'You do not hold a role in this sitting.',
  not_administrator:
    'The attendance register is kept by the Synod Admin. A Scrutineer may read a counted results sheet, but does not run the register.',
  roll_not_frozen:
    'The roll for this sitting has not been frozen yet. There is no register of attendees until there is a register of who may attend.',
  unknown_elector: 'That person is not on the roll for this sitting.',
  officer:
    'This person is an Officer of the Diocese. He keeps his House and his right to speak, to move and to second, and he has nothing to do with quorum — so he is not in this register. His attendance belongs in the minute book.',
  stood_down:
    'This member has been stood down and replaced under cl 8(3)(b). His seat is held by the substitute seated in his place, and the two of them resolve to ONE seat. Record the substitute instead.',
  chain_moved:
    'The register moved while you were reading it — somebody else recorded something. Nothing was written. Refresh and look again.',
  bad_request: 'That request was not understood.',
  elector_required: 'That request named nobody.',
  presence_required: 'A person is recorded present or absent, and nothing else.',
};

// The fallback names the REGISTER rather than the portal, and deliberately: the
// portal's name depends on who is reading (./portal-name.ts), and a sentence
// naming a different thing from the header above it reads as a fault. Every call
// that reaches here is a register call, so the register is the honest subject.
const refusalSentence = (code: string): string =>
  REFUSAL_COPY[code] ?? 'The register refused that. Nothing was written.';

type Screen =
  | { status: 'loading' }
  | { status: 'ready'; view: RegisterView }
  | { status: 'refused'; code: string };

interface Props {
  electionId: string;
  /** Only ever read for the header's name — the role word on it is the server's,
   *  and this screen's authorisation is re-resolved by the route on every
   *  request it serves. Passing it in is what stops this screen and the landing
   *  it was reached from wearing two different names. */
  identity: Identity;
  onBack: () => void;
}

export function RegisterPage({ electionId, identity, onBack }: Props): JSX.Element {
  const [screen, setScreen] = useState<Screen>({ status: 'loading' });
  /** The row currently being written, so its two buttons cannot be pressed
   *  twice. One at a time, deliberately: an append is an act. */
  const [pending, setPending] = useState<string | null>(null);
  /** The reason typed against a row, kept per elector. Only ever sent with the
   *  next append for THAT person. */
  const [reasons, setReasons] = useState<Record<string, string>>({});
  /** What the last act did, announced rather than flashed. */
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setScreen({ status: 'loading' });
    try {
      setScreen({ status: 'ready', view: await api.register(electionId) });
    } catch (err) {
      setScreen({
        status: 'refused',
        code: err instanceof DecidesApiError ? err.code : 'unreachable',
      });
    }
  }, [electionId]);

  useEffect(() => {
    void load();
  }, [load]);

  const record = useCallback(
    async (elector: RegisterElector, presence: Presence): Promise<void> => {
      setPending(elector.electorId);
      setNotice(null);
      const reason = (reasons[elector.electorId] ?? '').trim();
      try {
        const answer = await api.recordPresence(electionId, {
          electorId: elector.electorId,
          presence,
          ...(reason === '' ? {} : { reason }),
        });
        // The list and the counts come back from the SERVER's own fresh read.
        // Nothing here increments a number: an in-memory count is a second
        // answer to the question this screen exists to answer.
        setScreen((current) =>
          current.status === 'ready'
            ? {
                status: 'ready',
                view: {
                  ...current.view,
                  chain: { seq: answer.event.seq, headHash: answer.event.hash },
                  electors: answer.electors,
                  houses: answer.houses,
                },
              }
            : current,
        );
        setReasons((current) => ({ ...current, [elector.electorId]: '' }));
        setNotice(
          `Recorded ${PRESENCE_WORD[presence].toLowerCase()} for ${elector.electorId} at register position ${String(answer.event.seq)}.`,
        );
      } catch (err) {
        setNotice(refusalSentence(err instanceof DecidesApiError ? err.code : 'unreachable'));
        // A refusal about the CHAIN means this screen is looking at a register
        // that has moved, so it re-reads rather than leaving a stale list under
        // a message about staleness.
        if (err instanceof DecidesApiError && err.code === 'chain_moved') await load();
      } finally {
        setPending(null);
      }
    },
    [electionId, load, reasons],
  );

  if (screen.status === 'loading') {
    return (
      <Frame identity={identity} onBack={onBack}>
        <p role="status">Reading the register…</p>
      </Frame>
    );
  }

  if (screen.status === 'refused') {
    return (
      <Frame identity={identity} onBack={onBack}>
        <h2>The register is not open to you</h2>
        <p className="alert">{refusalSentence(screen.code)}</p>
        <button type="button" className="btn btn-ghost" onClick={() => void load()}>
          Try again
        </button>
      </Frame>
    );
  }

  const { view } = screen;

  return (
    <div className="register-app min-dvh">
      <PortalHeader portalName={portalNameFor(identity)} />
      <ModeBand mode={view.sitting.mode} />

      <main className="ballot-main">
        <div className="page-narrow">
          <button type="button" className="btn btn-ghost register-back" onClick={onBack}>
            Back to your sittings
          </button>

          <h1 className="ballot-title">{view.sitting.name}</h1>
          <p className="register-lede">
            Who is in the room, per House, as at now. Every entry is <strong>appended</strong>: a
            correction is a further entry, and both stay on the record.
          </p>

          <HouseCounts houses={view.houses} />

          {/* THE ONE SENTENCE THAT KEEPS THIS SCREEN INSIDE THE RULING. The
              numbers above are the register's own; the declaration is a separate
              act on a ballot, and nothing on this page performs it or judges it. */}
          <p className="register-note">
            These are the register&rsquo;s own figures. <strong>Quorum is declared by you</strong>,
            on the ballot, at the moment you enliven a vote — {portalNameFor(identity)} records that
            declaration and does not compute one.
          </p>

          {/* Announced, not flashed: a returning officer who has just recorded
              something needs to hear that it landed. `status`, never `alert` —
              a tick is not an alarm. */}
          <p className="register-notice" role="status">
            {notice ?? ''}
          </p>

          <RollList
            electors={view.electors}
            pending={pending}
            reasons={reasons}
            onReason={(id, value) => setReasons((current) => ({ ...current, [id]: value }))}
            onRecord={(elector, presence) => void record(elector, presence)}
          />

          <Provenance view={view} />
        </div>
      </main>

      <ModeBand mode={view.sitting.mode} repeat />
    </div>
  );
}

/** The page frame the loading and refusal screens share, so neither of them
 *  restates the layout. */
function Frame({
  children,
  identity,
  onBack,
}: {
  children: ReactNode;
  identity: Identity;
  onBack: () => void;
}): JSX.Element {
  return (
    <div className="register-app min-dvh">
      <PortalHeader portalName={portalNameFor(identity)} />
      <main className="ballot-main">
        <div className="page-narrow">
          <button type="button" className="btn btn-ghost register-back" onClick={onBack}>
            Back to your sittings
          </button>
          {children}
        </div>
      </main>
    </div>
  );
}

/**
 * The derived count, per House — the number the registrar said the Admin needs
 * "at every stage".
 *
 * MEASURES DOWN THE SIDE, HOUSES ACROSS THE TOP. That is the shape the results
 * sheet's withheld card had to be rewritten into: three long labels over three
 * short numbers blows the column's width at 390px on its own, and this table has
 * FOUR long labels. A real `<table>` at every width, never `.table-wrap` — a
 * comparison you must scroll sideways to make is not a comparison.
 *
 * NO TOTAL COLUMN AND NO TOTAL ROW. See the file header.
 */
function HouseCounts({ houses }: { houses: RegisterHouseCount[] }): JSX.Element {
  return (
    <table className="table register-table">
      <caption className="register-table-caption">
        The register as at now, per House. There is no total: a quorum is tested in each House
        separately (SO 12.1), and a number summed across them cannot be read against that rule.
      </caption>
      <thead>
        <tr>
          <th scope="col">
            <span className="ballot-sr">What is recorded</span>
          </th>
          {houses.map((house) => (
            <th scope="col" className="num" key={house.house}>
              {HOUSE_LABEL[house.house]}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        <tr>
          <th scope="row">On the roll</th>
          {houses.map((house) => (
            <td className="num" key={house.house}>
              {house.onRoll}
            </td>
          ))}
        </tr>
        <tr>
          <th scope="row">Present</th>
          {houses.map((house) => (
            <td className="num" key={house.house}>
              {house.present}
            </td>
          ))}
        </tr>
        <tr>
          <th scope="row">Absent</th>
          {houses.map((house) => (
            <td className="num" key={house.house}>
              {house.absent}
            </td>
          ))}
        </tr>
        <tr>
          {/* THE THIRD STATE, ON THE SCREEN, because this is who it is for: an
              Admin about to declare a quorum over a register with nine
              unrecorded electors is being asked for a number they do not have
              (0004_attendance.sql invariant 4). */}
          <th scope="row">Not yet recorded</th>
          {houses.map((house) => (
            <td className="num" key={house.house}>
              {house.unrecorded}
            </td>
          ))}
        </tr>
      </tbody>
    </table>
  );
}

interface RollListProps {
  electors: RegisterElector[];
  pending: string | null;
  reasons: Record<string, string>;
  onReason: (electorId: string, value: string) => void;
  onRecord: (elector: RegisterElector, presence: Presence) => void;
}

/** The roll itself, grouped by House. The list is the EFFECTIVE roll served by
 *  the route — the franchise the snapshot froze, minus the stand-downs — so a
 *  member replaced under a substitution is simply not here, and neither is an
 *  Officer. Nothing on this screen filters it again; a second filter in a
 *  browser is a second answer. */
function RollList({ electors, pending, reasons, onReason, onRecord }: RollListProps): JSX.Element {
  const houses: RegisterHouse[] = ['clergy', 'laity', 'bishop'];
  return (
    <>
      {houses.map((house) => {
        const members = electors.filter((elector) => elector.house === house);
        if (members.length === 0) return null;
        return (
          <section className="register-house" key={house} aria-labelledby={`house-${house}`}>
            <h2 className="result-subheading" id={`house-${house}`}>
              {HOUSE_LABEL[house]}
            </h2>
            <ul className="register-roll">
              {members.map((elector) => (
                <RollRow
                  key={elector.electorId}
                  elector={elector}
                  busy={pending === elector.electorId}
                  disabled={pending !== null}
                  reason={reasons[elector.electorId] ?? ''}
                  onReason={(value) => onReason(elector.electorId, value)}
                  onRecord={(presence) => onRecord(elector, presence)}
                />
              ))}
            </ul>
          </section>
        );
      })}
    </>
  );
}

interface RollRowProps {
  elector: RegisterElector;
  busy: boolean;
  disabled: boolean;
  reason: string;
  onReason: (value: string) => void;
  onRecord: (presence: Presence) => void;
}

/**
 * One person: who they are, what the register currently says, and the two
 * controls that append.
 *
 * **TWO BUTTONS, NOT A TOGGLE, AND NEVER A CHECKBOX.** A toggle implies a value
 * being flipped; these two each write a NEW row asserting a state. Pressing
 * "Present" on somebody already present is idempotent by construction — a
 * repeated assertion of the same state, which is exactly why
 * `attendance_event.presence` is a state and not a delta — so nothing here has
 * to guard against a double press beyond keeping the row busy.
 *
 * THE STATE IS A WORD AND A SHAPE, never a colour: `.ballot-dot` filled for
 * present, hollow for absent, and neither for unrecorded. Nothing in this
 * product is signalled by colour alone.
 *
 * THE REASON FIELD APPEARS ONLY FOR A CORRECTION, which is the schema's own
 * guidance: `attendance_event.reason` is nullable because "a tick at the door is
 * the ordinary act of the sitting and demanding prose for each one would make
 * the register slower to keep than to falsify. A CORRECTION usually carries one,
 * and the surface should ask."
 */
function RollRow({
  elector,
  busy,
  disabled,
  reason,
  onReason,
  onRecord,
}: RollRowProps): JSX.Element {
  const state = elector.presence ?? 'unrecorded';
  const isCorrection = elector.presence !== null;
  const reasonId = `reason-${elector.electorId}`;

  return (
    <li className="register-row">
      <div className="register-who">
        {/* THE IDENTIFIER IS ALL THERE IS. `elector` carries no name; see the
            file header and the README. */}
        <span className="register-id">{elector.contactId ?? elector.electorId}</span>
        <span className="register-state">
          {state === 'unrecorded' ? null : (
            <span
              className={`ballot-dot${state === 'absent' ? ' ballot-dot-hollow' : ''}`}
              aria-hidden="true"
            />
          )}
          {PRESENCE_WORD[state]}
        </span>
        {elector.recordedBy === null ? null : (
          <span className="register-provenance-line">
            Recorded by {elector.recordedBy}
            {elector.seq === null ? '' : ` at position ${String(elector.seq)}`}
            {elector.reason === null ? '' : ` — ${elector.reason}`}
          </span>
        )}
      </div>

      <div className="register-actions">
        <button
          type="button"
          className="btn register-btn"
          disabled={disabled}
          aria-label={`Record ${elector.contactId ?? elector.electorId} present`}
          onClick={() => onRecord('present')}
        >
          {busy ? '…' : 'Present'}
        </button>
        <button
          type="button"
          className="btn register-btn"
          disabled={disabled}
          aria-label={`Record ${elector.contactId ?? elector.electorId} absent`}
          onClick={() => onRecord('absent')}
        >
          {busy ? '…' : 'Absent'}
        </button>
      </div>

      {isCorrection ? (
        <div className="register-reason">
          <label htmlFor={reasonId}>Reason for a correction (optional)</label>
          <input
            id={reasonId}
            type="text"
            maxLength={500}
            value={reason}
            disabled={disabled}
            onChange={(event) => onReason(event.target.value)}
          />
        </div>
      ) : null}
    </li>
  );
}

/**
 * Where this register stands, and what it chains from — a native `<details>`,
 * closed by default, exactly as the results sheet discloses its digests.
 *
 * The roll digest is the GENESIS of both the attendance chain and the amendment
 * chain, and it is the value published to scrutineers. A person who holds it can
 * check that this register begins where the frozen roll ended.
 */
function Provenance({ view }: { view: RegisterView }): JSX.Element {
  return (
    <details className="register-provenance">
      <summary className="result-provenance-summary">Where this register stands</summary>
      <dl className="result-provenance-list">
        <div className="result-provenance-row">
          <dt>Entries recorded</dt>
          <dd>{view.chain.seq}</dd>
        </div>
        <div className="result-provenance-row">
          <dt>Frozen roll (the chain&rsquo;s genesis)</dt>
          <dd className="result-mono">{view.sitting.rollDigest}</dd>
        </div>
        <div className="result-provenance-row">
          <dt>Latest entry</dt>
          <dd className="result-mono">{view.chain.headHash ?? 'none yet'}</dd>
        </div>
      </dl>
      <p className="result-provenance-note">
        Every entry is linked to the one before it, and the first is linked to the roll digest
        published to the Scrutineers. Nothing is ever rewritten or removed: a correction is a
        further entry, so both what was recorded and what corrected it stay readable.
      </p>
    </details>
  );
}
